/**
 * How seriously to treat one of the outlook pipeline's reason codes.
 *
 * This exists because the card was answering the question twice. Headlines
 * judged by reason code, so CARE_HEAT read red; day rows judged by the plant's
 * weekly state, which stays `take_care` even when the code is CARE_HEAT, so the
 * same condition read amber a few pixels below. One function, used by both.
 *
 * It also turns a verdict token into the theme's text classes. Which token
 * a line is drawn in is the server's (each line carries it, from the
 * server's condition_colour table; batch 13a of the architecture audit,
 * A83): the app maps no palette name or reason code to a token.
 *
 * Who uses it: features/season SeasonHeader and features/weather
 * FrostOutlook (conditionTextClass).
 *
 * @module outlookSeverity
 */

import { TOKEN_TEXT, type VerdictToken } from '../theme/verdictTokens';

/** urgent: act now. caution: worth knowing. settled: nothing to do. */
export type OutlookSeverity = 'urgent' | 'caution' | 'settled';

/**
 * Classifies a single reason code.
 *
 * Mirrors how the pipeline scores them: leaving the SURVIVABLE band, or a frost
 * event, is weighted far more heavily than merely leaving OPTIMAL.
 *
 * @param code - e.g. CARE_WARM, CARE_HEAT, PROTECT_FROST, needs_water.
 * @returns urgent for a danger, settled for all clear or enough rain, caution otherwise.
 */
export function severityForReason(code: string): OutlookSeverity {
  if (code.startsWith('PROTECT_') || code === 'CARE_HEAT' || code === 'CARE_COLD') {
    return 'urgent';
  }
  if (code === 'ALL_CLEAR' || code === 'covered_by_rain' || code === 'surplus') {
    return 'settled';
  }
  return 'caution';
}

/**
 * Text colour for a condition, in the theme's own verdict colours.
 *
 * Colour says direction and depth says severity: the server says which
 * token a line is drawn in, and this draws it. It used to colour by severity
 * alone, which made "too cold" red and "chilly" amber: a cold warning in a
 * warm colour, contradicting the temperature reading printed beside it.
 *
 * @param token - The token the server sent with the line; none for the ink.
 * @returns Tailwind text classes, neutral when there is no token.
 */
export function conditionTextClass(token: VerdictToken | null | undefined): string {
  return token ? TOKEN_TEXT[token] : 'text-muted-foreground';
}

/**
 * Which way a condition leans: towards cold, towards heat, or neither.
 *
 * Read off the palette colour rather than the reason code, because the colour
 * is where the pipeline already states direction — cold deepening cyan, blue,
 * indigo; heat deepening amber, red. Wind and water are real conditions with
 * no temperature direction at all, and saying "protect from cold" over a hail
 * warning would be worse than saying nothing.
 */
export type ConditionDirection = 'cold' | 'heat' | 'neither';

/** Palette names that lean cold, chilly to freezing. */
const COLD_KEYS = new Set(['cyan', 'blue', 'indigo']);
/** Palette names that lean hot, warm to dangerous. */
const HEAT_KEYS = new Set(['amber', 'orange', 'red']);

/**
 * The direction a palette colour leans.
 *
 * @param colorKey - The palette name the outlook sent.
 * @returns cold, heat or neither.
 */
export function directionForColor(colorKey: string | null | undefined): ConditionDirection {
  if (!colorKey) return 'neither';
  if (COLD_KEYS.has(colorKey)) return 'cold';
  if (HEAT_KEYS.has(colorKey)) return 'heat';
  return 'neither';
}

